import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Loading, Msg, Section, fmtDate, panelGet, panelPost } from '../../kit';
import { AdminLink } from '../../adminNav';
import { Badge, Check, Drawer, EmailPreview, Labeled, Modal, Note, fmtFrom, inputCls } from '../emailKit';
import { ContentEditor, FallbacksPanel, fallbackable } from '../content/ContentEditor';
import { EnvelopeFields } from '../content/EnvelopeFields';
import { VaryingNotices } from '../VaryingNotice';
import { SaveAsTemplate, TemplatePicker } from '../content/TemplatePicker';
import {
  EMPTY_EDITABLE, EMPTY_ENVELOPE, EditableContent, Envelope, FORMAT_LABEL, LibraryMeta, LibraryRow,
  Rendered, SourceContent, SourceRef, UsedBy, contentFields, contentProblem, fromLibraryRow, fromSource,
  renderedFor, toLibraryPayload,
} from '../content/types';

/**
 * One saved template (backend: email_library — library_get / library_save /
 * library_archive / library_restore / library_version / library_render /
 * library_test_send).
 *
 * Every save is a new version (the last 50 are kept; any can be viewed and
 * restored). A template a switched-on MEMAC trigger sends is live: saving it
 * changes that trigger's next mail, so the server asks for a confirm and the
 * editor shows which triggers. Archive hides it from the pickers and is
 * refused while a trigger points at it; old records keep its name.
 *
 * The preview is the server's render of what is in the editor (saved or not)
 * with sample values or one member's, its links samples, so a click in it
 * can neither unsubscribe anyone nor count as a click. "Send test to me"
 * mails only the calling admin, with "[Test]" in the subject.
 *
 * Its fallbacks (what a placeholder becomes for someone with no value) are
 * kept with it: a campaign or MEMAC trigger sending it gets them, under any
 * of its own.
 */

export type Seed = {
  content: EditableContent; envelope: Envelope; source: SourceRef | null;
  name: string; category: string; description?: string; fallbacks?: Record<string, string>; nonce: number;
};
type Form = {
  name: string; description: string; category: string; content: EditableContent; envelope: Envelope;
  fallbacks: Record<string, string>;
};
type Version = { version: number; saved_by: string; saved_at: string; note: string };
type LiveConfirm = { action: 'save' | 'restore'; version?: number; text: string };

const SERVICE = ['service', 'billing'];
const sig = (f: Form) => JSON.stringify({ ...f, content: contentFields(f.content), envelope: f.envelope });
const blankForm = (): Form => ({
  name: '', description: '', category: '', content: { ...EMPTY_EDITABLE }, envelope: { ...EMPTY_ENVELOPE },
  fallbacks: {},
});
const formOf = (t: LibraryRow): Form => {
  const { content, envelope, fallbacks } = fromLibraryRow(t);
  return { name: t.name, description: t.description || '', category: t.category || '', content, envelope, fallbacks };
};

export const LibraryTemplateEditor: React.FC<{
  /** The saved template, or null for a new one (started from `seed`). */
  templateKey: string | null;
  meta: LibraryMeta | null;
  origin: 'library' | 'campaigns' | 'memac';
  seed?: Seed | null;
  /** After a save (new or edit) and a duplicate: the list reloads and selects it. */
  onSaved: (t: LibraryRow) => void;
  /** After an archive / unarchive / restore: the list reloads. */
  onChanged: (t: LibraryRow) => void;
  onDirty?: (dirty: boolean) => void;
  compact?: boolean;
}> = ({ templateKey, meta, origin, seed, onSaved, onChanged, onDirty, compact }) => {
  const [row, setRow] = useState<LibraryRow | null>(null);
  const [usedBy, setUsedBy] = useState<UsedBy | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Form>(blankForm);
  const [base, setBase] = useState(() => sig(blankForm()));
  const [source, setSource] = useState<SourceRef | null>(null);
  const [msg, setMsg] = useState<Note>(null);
  const [clash, setClash] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState('');
  const [picker, setPicker] = useState(false);
  const [live, setLive] = useState<LiveConfirm | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const [restoreV, setRestoreV] = useState<number | null>(null);
  const [dup, setDup] = useState<SourceContent | null>(null);
  const [vView, setVView] = useState<{ version: number; snap: any; rendered: Rendered | null; err: string } | null>(null);

  const dirty = sig(form) !== base;
  useEffect(() => { onDirty?.(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async (key: string) => {
    setLoading(true); setLoadErr('');
    const { data, error } = await panelGet('email', 'library_get', { key });
    setLoading(false);
    if (error) { setLoadErr(error); return; }
    const d: any = data;
    const t = d.template as LibraryRow;
    const f = formOf(t);
    setRow(t); setUsedBy(d.used_by || t.used_by || null); setVersions(d.versions || []);
    setForm(f); setBase(sig(f)); setSource(null); setClash(false);
  };
  // A new template just saved comes back as its key: keep the note about it.
  const justSaved = useRef('');
  useEffect(() => {
    if (!templateKey || justSaved.current !== templateKey) { setMsg(null); setWarnings([]); }
    justSaved.current = '';
    setVView(null);
    if (templateKey) { load(templateKey); return; }
    setRow(null); setUsedBy(null); setVersions([]); setLoadErr(''); setClash(false);
    const f: Form = seed
      ? { name: seed.name, description: seed.description || '', category: seed.category, content: seed.content,
        envelope: seed.envelope, fallbacks: { ...(seed.fallbacks || {}) } }
      : blankForm();
    setForm(f); setBase(sig(blankForm())); setSource(seed?.source || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, seed?.nonce]);

  const patch = (p: Partial<Form>) => setForm(f => ({ ...f, ...p }));
  const archived = !!row?.archived;
  const liveTriggers = (usedBy?.triggers || []).filter(t => t.enabled && t.uses !== false);
  const problem = !form.name.trim() ? 'Give the template a name.' : contentProblem(form.content);

  // -- preview: the server's render of the editor's contents --------------------
  const [memacLook, setMemacLook] = useState(origin === 'memac');
  const [previewEmail, setPreviewEmail] = useState('');
  const [previewFor, setPreviewFor] = useState('');
  const [view, setView] = useState<Rendered | null>(null);
  const [viewErr, setViewErr] = useState('');
  const [rendering, setRendering] = useState(false);
  const seq = useRef(0);
  const memacCat = memacLook && form.category && !SERVICE.includes(form.category) ? form.category : '';
  const draft = useMemo(() => ({
    ...contentFields(form.content), name: form.name || 'Draft', category: form.category,
    from_email: form.envelope.from_email, from_name: form.envelope.from_name, reply_to: form.envelope.reply_to,
    placeholder_fallbacks: form.fallbacks,
  }), [form]);
  const draftSig = JSON.stringify(draft);
  const contentOk = !contentProblem(form.content);
  useEffect(() => {
    if (!contentOk) { setView(null); setViewErr(''); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setRendering(true);
      const body: Record<string, any> = { draft };
      if (row?.key) body.key = row.key;
      if (previewFor) body.user_email = previewFor;
      if (memacCat) body.memac_category = memacCat;
      const { data, error } = await panelPost('email', 'library_render', body);
      if (mine !== seq.current) return;
      setRendering(false);
      if (error) { setViewErr(error); return; }
      setViewErr(''); setView(data as Rendered);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSig, previewFor, memacCat, contentOk, row?.key]);

  // -- actions ----------------------------------------------------------------
  const save = async (confirmLive = false) => {
    if (problem || busy) return;
    setBusy('save'); setMsg(null); setWarnings([]); setClash(false);
    const { data, error } = await panelPost('email', 'library_save', toLibraryPayload(form.content, form.envelope, {
      key: row?.key, name: form.name, description: form.description, category: form.category, origin,
      source, fallbacks: form.fallbacks, expected_version: row?.version, confirm_live: confirmLive,
    }));
    setBusy('');
    if (error) {
      if (error.startsWith('confirm_live required')) { setLive({ action: 'save', text: error }); return; }
      if (/changed since you opened it/.test(error)) setClash(true);
      setMsg({ kind: 'error', text: error });
      return;
    }
    const t = (data as any).template as LibraryRow;
    setWarnings((data as any).warnings || []);
    setMsg({ kind: 'ok', text: row ? `Saved “${t.name}” as v${t.version}.` : `Saved “${t.name}”. It is now in every template list.` });
    if (row) await load(t.key);
    else justSaved.current = t.key;
    onSaved(t);
  };

  const archive = async () => {
    if (!row || busy) return;
    if (!row.archived && !window.confirm(`Archive “${row.name}”? It leaves the template lists; campaigns already sent keep their record. You can unarchive it.`)) return;
    setBusy('archive'); setMsg(null);
    const { data, error } = await panelPost('email', 'library_archive', { key: row.key, archived: !row.archived });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    const t = (data as any).template as LibraryRow;
    setMsg({ kind: 'ok', text: t.archived ? `Archived “${t.name}”.` : `“${t.name}” is back in the template lists.` });
    await load(t.key);
    onChanged(t);
  };

  const restore = async (version: number, confirmLive = false) => {
    if (!row || busy) return;
    setRestoreV(null); setBusy('restore'); setMsg(null);
    const { data, error } = await panelPost('email', 'library_restore', {
      key: row.key, version, expected_version: row.version, ...(confirmLive ? { confirm_live: true } : {}) });
    setBusy('');
    if (error) {
      if (error.startsWith('confirm_live required')) { setLive({ action: 'restore', version, text: error }); return; }
      if (/changed since you opened it/.test(error)) setClash(true);
      setMsg({ kind: 'error', text: error });
      return;
    }
    const t = (data as any).template as LibraryRow;
    setMsg({ kind: 'ok', text: `Restored v${version} as v${t.version}.` });
    setVView(null);
    await load(t.key);
    onChanged(t);
  };

  const viewVersion = async (version: number) => {
    if (!row) return;
    setVView({ version, snap: null, rendered: null, err: '' });
    const { data, error } = await panelGet('email', 'library_version', { key: row.key, version });
    if (error) { setVView({ version, snap: null, rendered: null, err: error }); return; }
    const snap = (data as any).snapshot || {};
    const r = await panelPost('email', 'library_render', { key: row.key, draft: {
      body_format: snap.format, subject: snap.subject, body: snap.body, text: snap.text, design: snap.design,
      ctas: snap.ctas, category: snap.category, name: snap.name,
      from_email: snap.from_email, from_name: snap.from_name, reply_to: snap.reply_to,
      placeholder_fallbacks: snap.placeholder_fallbacks || {} } });
    setVView({ version, snap, rendered: r.error ? null : (r.data as Rendered), err: r.error });
  };
  const loadVersion = () => {
    const s = vView?.snap;
    if (!s || !row) return;
    const f = formOf({ ...row, ...s, format: s.format, design: s.design || null, ctas: s.ctas || [],
      placeholder_fallbacks: s.placeholder_fallbacks || {} } as LibraryRow);
    setForm({ ...f, name: form.name });
    setVView(null);
    setMsg({ kind: 'ok', text: `v${vView?.version} is in the editor, not saved. Save to make it the current version.` });
  };

  const duplicate = async () => {
    if (!row || busy) return;
    setBusy('dup'); setMsg(null);
    const { data, error } = await panelGet('email', 'library_source', { kind: 'library', ref: row.key });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setDup(data as SourceContent);
  };

  const sendTest = async () => {
    setTestOpen(false); setBusy('test'); setMsg(null);
    const body: Record<string, any> = { draft, confirm: true };
    if (row?.key) body.key = row.key;
    if (memacCat) body.memac_category = memacCat;
    const { data, error } = await panelPost('email', 'library_test_send', body);
    setBusy('');
    const d: any = data;
    if (error || d?.sent === false) { setMsg({ kind: 'error', text: error || d?.error || 'The test e-mail was not sent.' }); return; }
    setMsg({ kind: 'ok', text: `Test sent to ${d?.to || 'your admin address'}: “${d?.subject || form.name}”.` });
  };

  const fromPicker = (src: SourceContent) => {
    const s = fromSource(src);
    setPicker(false);
    if (dirty && !window.confirm('Replace what is in the editor with this e-mail? Your unsaved changes are lost.')) return;
    setForm(f => ({ ...f, content: s.content, envelope: s.envelope, category: f.category || s.category,
      name: f.name || src.name, fallbacks: s.fallbacks }));
    if (!row) setSource(s.source);
    setWarnings([...s.warnings, ...(src.notes || [])]);
    setMsg({ kind: 'ok', text: `Copied “${src.name}” into the editor. Nothing is saved yet.` });
  };

  // -- render -----------------------------------------------------------------
  if (templateKey && loading && !row) return <Loading />;
  if (templateKey && loadErr && !row) return <Msg kind="error">{loadErr}</Msg>;
  const cats = meta?.categories || [];
  const catLabel = (k: string) => cats.find(c => c.key === k)?.label || k;
  const src = row?.source || source;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`${compact ? 'text-sm' : 'text-base'} font-bold text-grayscale-1300 font-grotesk`}>
            {row ? row.name : 'New saved template'}
          </span>
          {row && <code className="text-[11px] text-grayscale-1100">{row.key}</code>}
          {row && <Badge tone="grey">v{row.version}</Badge>}
          <Badge tone="grey">{FORMAT_LABEL[form.content.format]}</Badge>
          {archived && <Badge tone="red">archived</Badge>}
          {liveTriggers.length > 0 && <Badge tone="amber">live in {liveTriggers.length} switched-on trigger{liveTriggers.length === 1 ? '' : 's'}</Badge>}
          {dirty && <Badge tone="amber">unsaved changes</Badge>}
        </div>
        {row && (
          <div className="text-[11px] text-grayscale-1100">
            Made in {row.origin === 'memac' ? 'MEMAC' : row.origin === 'campaigns' ? 'Campaigns' : 'Saved templates'}
            {row.created_by ? ` by ${row.created_by}` : ''}{row.created_at ? `, ${fmtDate(row.created_at)}` : ''}
            {row.updated_at ? ` · last saved ${fmtDate(row.updated_at)}${row.updated_by ? ` by ${row.updated_by}` : ''}` : ''}
          </div>
        )}
        {src?.name && (
          <div className="text-[11px] text-grayscale-1100">
            Started from {src.kind === 'campaign' ? 'the campaign' : src.kind === 'design' ? 'the design' : src.kind === 'trigger' ? 'MEMAC trigger' : ''} “{src.name}”{src.version ? ` (v${src.version})` : ''}
          </div>
        )}
      </div>

      {archived && (
        <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
          Archived: it is not offered in any template list. Unarchive it to edit it or restore a version.
        </div>
      )}

      <fieldset disabled={archived} className="flex flex-col gap-3 min-w-0">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem]">
          <Labeled label="Name (shown in every template list)">
            <input value={form.name} maxLength={meta?.limits?.name || 120} onChange={e => patch({ name: e.target.value })}
              placeholder="e.g. Trial ending — personal note" className={inputCls} />
          </Labeled>
          <Labeled label="Category">
            <select value={form.category} onChange={e => patch({ category: e.target.value })} className={inputCls}>
              <option value="">No category</option>
              {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Labeled>
        </div>
        <Labeled label="Description (optional — what it is for)">
          <input value={form.description} maxLength={meta?.limits?.description || 500}
            onChange={e => patch({ description: e.target.value })} className={inputCls} />
        </Labeled>
        <div className="text-[11px] text-grayscale-1100 -mt-1">
          The category decides who can unsubscribe from it when Campaigns send it (a MEMAC trigger uses its own
          category). Service and billing mail cannot be unsubscribed from.
        </div>
      </fieldset>

      <ContentEditor value={form.content} onChange={p => setForm(f => ({ ...f, content: { ...f.content, ...p } }))}
        formats={['text', 'html', 'design']} placeholders={meta?.placeholders || []} allowSaveAsDesign
        saveAsDesignNote={`From the saved template “${form.name || 'new'}”`} disabled={archived}
        hint={<div className="text-[11px] text-grayscale-1100">
          Plain text is how the built-in e-mails are written (words + up to 3 buttons); Rich text is Campaigns'
          “Write from scratch”; Design is the block builder.
        </div>} />

      <FallbacksPanel names={fallbackable(form.content)} value={form.fallbacks} disabled={archived}
        onChange={fb => setForm(f => ({ ...f, fallbacks: fb }))}
        title="When someone has no value (a trigger or campaign sending it can set its own on top)" />

      <div className="border-t border-grayscale-200 pt-3">
        <EnvelopeFields value={form.envelope} onChange={p => setForm(f => ({ ...f, envelope: { ...f.envelope, ...p } }))}
          senders={meta?.senders || []} disabled={archived} />
        <div className="text-[11px] text-grayscale-1100 mt-1">
          Left blank, it is sent from Founders inbox@ with replies to info@. A trigger or campaign may set its own sender.
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <Btn kind="primary" onClick={() => save(false)} disabled={archived || !!busy || !!problem || (!!row && !dirty)}
          title={problem || undefined}>
          {busy === 'save' ? 'Saving…' : row ? 'Save' : 'Save template'}
        </Btn>
        {dirty && row && <Btn onClick={() => { const f = formOf(row); setForm(f); setBase(sig(f)); }} disabled={!!busy}>Discard changes</Btn>}
        <Btn onClick={() => setPicker(true)} disabled={archived || !!busy}
          title="Copy a saved template, a built-in e-mail, a past campaign or a design into the editor">Start from…</Btn>
        {row && <Btn onClick={duplicate} disabled={!!busy}>{busy === 'dup' ? 'Loading…' : 'Duplicate'}</Btn>}
        {row && (
          <Btn onClick={archive} disabled={!!busy} kind={row.archived ? 'subtle' : 'danger'}>
            {busy === 'archive' ? '…' : row.archived ? 'Unarchive' : 'Archive'}
          </Btn>
        )}
        <Btn onClick={() => setTestOpen(true)} disabled={!!busy || !contentOk}
          title="Mail what is in the editor to your own admin address">
          {busy === 'test' ? 'Sending…' : 'Send test to me'}
        </Btn>
        {problem && <span className="text-xs text-grayscale-1100">{problem}</span>}
      </div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {clash && row && (
        <div className="flex items-center gap-2 text-xs text-grayscale-1200">
          Someone saved it since you opened it.
          <Btn small onClick={() => { if (!dirty || window.confirm('Reload the latest version? Your unsaved changes are lost.')) load(row.key); }}>
            Reload the latest
          </Btn>
        </div>
      )}

      <Section title="Preview"
        right={<span className="text-xs text-grayscale-1100">{rendering ? 'Rendering…' : `With ${renderedFor(view)}`}</span>}>
        <div className="grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)] min-w-0">
          <div className="flex flex-col gap-3 min-w-0">
            <div className="flex items-end gap-2 flex-wrap">
              <Labeled label="Preview as">
                <input value={previewEmail} onChange={e => setPreviewEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') setPreviewFor(previewEmail.trim().toLowerCase()); }}
                  placeholder="a member's e-mail (blank = sample values)" className={inputCls} />
              </Labeled>
              <Btn small onClick={() => setPreviewFor(previewEmail.trim().toLowerCase())}>Use</Btn>
              {previewFor && <Btn small onClick={() => { setPreviewEmail(''); setPreviewFor(''); }}>Sample values</Btn>}
            </div>
            <Check checked={memacLook} onChange={setMemacLook}>As MEMAC sends it (adds the unsubscribe line)</Check>
            {memacLook && !memacCat && (
              <div className="text-[11px] text-grayscale-1100">
                {form.category ? 'Service and billing mail gets no unsubscribe line.' : 'Pick a category to see the line.'}
              </div>
            )}
            {view?.warnings && view.warnings.length > 0 && (
              <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                {view.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}
            {view && <VaryingNotices varying={view.varying} />}
            {view?.blank && view.blank.length > 0 && (
              <div className="text-[11px] text-grayscale-1100">
                Blank for this person: {view.blank.map(b => `{${b}}`).join(', ')}
              </div>
            )}
            <div className="text-[11px] text-grayscale-1100">
              Links in the preview are samples: clicking one neither unsubscribes anyone nor counts as a click.
            </div>

            {row && (
              <div className="flex flex-col gap-1 border-t border-grayscale-200 pt-2">
                <div className="text-xs font-bold text-grayscale-1300">Used by</div>
                {(usedBy?.triggers || []).length ? (usedBy!.triggers.map(t => (
                  <div key={t.id} className="text-xs text-grayscale-1200 flex items-center gap-1.5 flex-wrap">
                    <AdminLink to={{ group: 'email', tab: 'memac', trigger: t.id }}>{t.id} · {t.name}</AdminLink>
                    {t.enabled ? <Badge tone="green">on</Badge> : <Badge tone="grey">off</Badge>}
                    {t.uses === false && <Badge tone="grey">uses its own copy</Badge>}
                  </div>
                ))) : <div className="text-xs text-grayscale-1100">No MEMAC trigger.</div>}
                <div className="text-xs text-grayscale-1100">
                  {usedBy?.campaigns ? `${usedBy.campaigns} campaign${usedBy.campaigns === 1 ? '' : 's'}` : 'No campaign yet'}
                  {usedBy?.last_sent_at ? ` · last sent ${fmtDate(usedBy.last_sent_at)}` : ''}
                </div>
              </div>
            )}
            {row && versions.length > 0 && (
              <div className="flex flex-col gap-1 border-t border-grayscale-200 pt-2">
                <div className="text-xs font-bold text-grayscale-1300">Versions</div>
                <div className="flex flex-col gap-0.5 max-h-60 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.version} className="text-[11px] text-grayscale-1200 flex items-center gap-1.5 flex-wrap">
                      <b>v{v.version}</b> {v.note} · {fmtDate(v.saved_at)}{v.saved_by ? ` · ${v.saved_by}` : ''}
                      <button type="button" className="underline underline-offset-2 text-primary-og-600"
                        onClick={() => viewVersion(v.version)}>view</button>
                      {v.version !== row.version && !archived && (
                        <button type="button" className="underline underline-offset-2 text-primary-og-600"
                          onClick={() => setRestoreV(v.version)}>restore</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="min-w-0">
            {!contentOk ? (
              <div className="text-sm text-grayscale-1100 py-6 text-center">{contentProblem(form.content)}</div>
            ) : viewErr ? <Msg kind="error">Preview: {viewErr}</Msg>
              : view ? (
                <EmailPreview subject={view.subject} html={view.html} text={view.text}
                  from={fmtFrom(view.from_name, view.from_email)} replyTo={view.reply_to || undefined}
                  to={typeof view.rendered_for === 'object' && view.rendered_for ? view.rendered_for.email : undefined}
                  banner={dirty ? <div className="text-xs text-amber-800">Unsaved edits — rendered exactly as they would go out.</div> : undefined} />
              ) : <Loading label="Rendering…" />}
          </div>
        </div>
      </Section>

      {picker && (
        <TemplatePicker onClose={() => setPicker(false)} onPick={src => fromPicker(src)}
          title="Start from… (copied into this template)" />
      )}
      {vView && (
        <Drawer title={`${row?.name || 'Template'} · v${vView.version}`} onClose={() => setVView(null)}
          subtitle={versions.find(v => v.version === vView.version)?.note}>
          {vView.err && <Msg kind="error">{vView.err}</Msg>}
          {!vView.snap && !vView.err && <Loading />}
          {vView.snap && (
            <div className="flex flex-wrap gap-2 items-center">
              <Badge tone="grey">{FORMAT_LABEL[vView.snap.format as keyof typeof FORMAT_LABEL] || vView.snap.format}</Badge>
              {vView.snap.category && <Badge tone="grey">{catLabel(vView.snap.category)}</Badge>}
              {!archived && <Btn small onClick={loadVersion}>Put it in the editor</Btn>}
              {!archived && row && vView.version !== row.version && (
                <Btn small kind="primary" onClick={() => setRestoreV(vView.version)}>Restore it</Btn>
              )}
            </div>
          )}
          {vView.rendered && (
            <EmailPreview subject={vView.rendered.subject} html={vView.rendered.html} text={vView.rendered.text}
              from={fmtFrom(vView.rendered.from_name, vView.rendered.from_email)}
              replyTo={vView.rendered.reply_to || undefined} />
          )}
        </Drawer>
      )}
      {dup && row && (
        <SaveAsTemplate content={fromSource(dup).content} envelope={fromSource(dup).envelope}
          fallbacks={fromSource(dup).fallbacks} origin={origin}
          source={fromSource(dup).source} defaultName={`${row.name} (copy)`} defaultCategory={row.category}
          defaultDescription={row.description} title={`Duplicate “${row.name}”`}
          note="The copy is a new template with its own versions; the original is not touched."
          onClose={() => setDup(null)}
          onSaved={(t, w) => {
            setDup(null); justSaved.current = t.key; setWarnings(w);
            setMsg({ kind: 'ok', text: `Duplicated as “${t.name}”. This is the copy.` });
            onSaved(t);
          }} />
      )}
      {live && (
        <Modal title="This template is live" onClose={() => setLive(null)}
          footer={<>
            <Btn onClick={() => setLive(null)}>Cancel</Btn>
            <Btn kind="primary" onClick={() => {
              const l = live; setLive(null);
              if (l.action === 'save') save(true); else if (l.version) restore(l.version, true);
            }}>{live.action === 'save' ? 'Save — the next send uses it' : `Restore v${live.version} — the next send uses it`}</Btn>
          </>}>
          <p>Switched-on MEMAC triggers send this template, so their next e-mails use your change:</p>
          <ul className="list-disc pl-5 text-xs">
            {liveTriggers.length ? liveTriggers.map(t => <li key={t.id}>{t.id} · {t.name}</li>)
              : <li>{live.text}</li>}
          </ul>
          <p className="text-xs text-grayscale-1100">The current version is kept: you can restore it afterwards.</p>
        </Modal>
      )}
      {restoreV !== null && row && (
        <Modal title={`Restore v${restoreV}?`} onClose={() => setRestoreV(null)}
          footer={<>
            <Btn onClick={() => setRestoreV(null)}>Cancel</Btn>
            <Btn kind="primary" onClick={() => restore(restoreV)}>Restore as a new version</Btn>
          </>}>
          <p>
            Its content, sender, fallbacks and category are saved as v{row.version + 1}; the name stays. Nothing is lost:
            v{row.version} stays in the list.
          </p>
          {dirty && <p className="text-amber-700">Your unsaved changes in the editor are discarded.</p>}
        </Modal>
      )}
      {testOpen && (
        <Modal title="Send a test to yourself?" onClose={() => setTestOpen(false)}
          footer={<>
            <Btn onClick={() => setTestOpen(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={sendTest}>Send test</Btn>
          </>}>
          <p>
            Sends what is in the editor to <b>your own admin address</b> only, with “[Test]” in front of the subject,
            filled with your account's values. Nobody else receives it, and it is not recorded as a send.
          </p>
          {dirty && <p className="text-xs text-grayscale-1100">Your unsaved changes are included; sending a test does not save them.</p>}
        </Modal>
      )}
    </div>
  );
};

export default LibraryTemplateEditor;
